import { useState, useEffect, type FormEvent } from 'react'
import {
    Box,
    Typography,
    Card,
    CardContent,
    TextField,
    Button,
    Grid,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    FormControlLabel,
    Switch,
    Divider,
    Alert,
    Chip,
} from '@mui/material'
import { providerForCountry, retentionPolicy } from '@yourownmetric/shared'

import { useAppSelector } from '@/app/hooks'
import { useGetTenantQuery, useUpdateTenantMutation } from '@/apis/settingsApi'

import { CloseOrganisationCard } from './CloseOrganisationCard'

export function SettingsPage() {
    const tenantId = useAppSelector(s => s.auth.tenant?.id) ?? ''
    const { data: tenant, isLoading } = useGetTenantQuery(tenantId, { skip: !tenantId })
    const [updateTenant, { isLoading: isSaving, isSuccess }] = useUpdateTenantMutation()

    const settings = tenant?.settings ? JSON.parse(tenant.settings) : {}
    // Plan retention (RETENTION_POLICY): Free 180 days; PAYG 395 by default, up to 5 years.
    const retention = retentionPolicy(tenant?.plan)
    const retentionOptions = [...new Set([30, 90, 180, 395, 730, 1825, retention.defaultDays])]
        .filter(d => d <= retention.maxDays)
        .sort((a, b) => a - b)

    const [name, setName] = useState('')
    const [country, setCountry] = useState('US')
    const [timezone, setTimezone] = useState('UTC')
    const [dataRetention, setDataRetention] = useState(retention.defaultDays)
    const [ipAnon, setIpAnon] = useState<'hash' | 'truncate' | 'discard'>('hash')
    const [cookieless, setCookieless] = useState(false)
    const [botFiltering, setBotFiltering] = useState(true)

    useEffect(() => {
        if (tenant) {
            setName(tenant.name)
            setCountry(tenant.country ?? 'US')
            setTimezone(settings.timezone ?? 'UTC')
            setDataRetention(
                Math.min(settings.dataRetentionDays ?? retention.defaultDays, retention.maxDays),
            )
            setIpAnon(settings.ipAnonymization ?? 'hash')
            setCookieless(settings.cookieless ?? false)
            setBotFiltering(settings.botFiltering ?? true)
        }
    }, [tenant]) // eslint-disable-line react-hooks/exhaustive-deps

    const handleSave = async (e: FormEvent) => {
        e.preventDefault()
        await updateTenant({
            tenantId,
            name,
            country,
            settings: {
                timezone,
                dataRetentionDays: dataRetention,
                ipAnonymization: ipAnon,
                cookieless,
                botFiltering,
            },
        }).unwrap()
    }

    if (isLoading) {
        return (
            <Box sx={{ p: 3 }}>
                <Typography>Loading settings…</Typography>
            </Box>
        )
    }

    return (
        <Box sx={{ p: 3, maxWidth: 800 }}>
            <Typography variant="h5" sx={{ mb: 3 }}>
                Settings
            </Typography>

            {isSuccess && (
                <Alert severity="success" sx={{ mb: 3 }}>
                    Settings saved successfully.
                </Alert>
            )}

            <Box component="form" onSubmit={handleSave}>
                {/* General Settings */}
                <Card sx={{ mb: 3 }}>
                    <CardContent>
                        <Typography variant="h6" sx={{ mb: 2 }}>
                            General
                        </Typography>
                        <Grid container spacing={2}>
                            <Grid size={{ xs: 12, md: 6 }}>
                                <TextField
                                    label="Workspace Name"
                                    value={name}
                                    onChange={e => setName(e.target.value)}
                                    fullWidth
                                />
                            </Grid>
                            <Grid size={{ xs: 12, md: 6 }}>
                                <TextField
                                    label="Slug"
                                    value={tenant?.slug ?? ''}
                                    fullWidth
                                    disabled
                                    helperText="Cannot be changed"
                                />
                            </Grid>
                            <Grid size={{ xs: 12, md: 6 }}>
                                <FormControl fullWidth>
                                    <InputLabel>Timezone</InputLabel>
                                    <Select
                                        value={timezone}
                                        label="Timezone"
                                        onChange={e => setTimezone(e.target.value)}
                                    >
                                        {[
                                            'UTC',
                                            'America/New_York',
                                            'America/Los_Angeles',
                                            'Europe/London',
                                            'Europe/Berlin',
                                            'Asia/Kolkata',
                                            'Asia/Tokyo',
                                        ].map(tz => (
                                            <MenuItem key={tz} value={tz}>
                                                {tz}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid size={{ xs: 12, md: 6 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pt: 2 }}>
                                    <Typography variant="body2" color="text.secondary">
                                        Plan:
                                    </Typography>
                                    <Chip
                                        label={tenant?.plan ?? 'free'}
                                        color="primary"
                                        size="small"
                                        sx={{ textTransform: 'capitalize' }}
                                    />
                                </Box>
                            </Grid>
                            <Grid size={{ xs: 12, md: 6 }}>
                                <FormControl fullWidth>
                                    <InputLabel>Country</InputLabel>
                                    <Select
                                        value={country}
                                        label="Country"
                                        onChange={e => setCountry(e.target.value)}
                                    >
                                        {[
                                            { code: 'US', label: 'United States' },
                                            { code: 'GB', label: 'United Kingdom' },
                                            { code: 'DE', label: 'Germany' },
                                            { code: 'FR', label: 'France' },
                                            { code: 'IN', label: 'India' },
                                            { code: 'JP', label: 'Japan' },
                                            { code: 'AU', label: 'Australia' },
                                            { code: 'CA', label: 'Canada' },
                                            { code: 'BR', label: 'Brazil' },
                                            { code: 'SG', label: 'Singapore' },
                                        ].map(c => (
                                            <MenuItem key={c.code} value={c.code}>
                                                {c.label} ({c.code})
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid size={{ xs: 12, md: 6 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pt: 2 }}>
                                    <Typography variant="body2" color="text.secondary">
                                        Billing provider:
                                    </Typography>
                                    <Chip
                                        label={providerForCountry(country)}
                                        color={
                                            providerForCountry(country) === 'razorpay'
                                                ? 'warning'
                                                : 'info'
                                        }
                                        size="small"
                                        sx={{ textTransform: 'capitalize' }}
                                    />
                                </Box>
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>

                {/* Privacy Settings */}
                <Card sx={{ mb: 3 }}>
                    <CardContent>
                        <Typography variant="h6" sx={{ mb: 2 }}>
                            Privacy &amp; Compliance
                        </Typography>
                        <Grid container spacing={2}>
                            <Grid size={{ xs: 12, md: 6 }}>
                                <FormControl fullWidth>
                                    <InputLabel>IP Anonymization</InputLabel>
                                    <Select
                                        value={ipAnon}
                                        label="IP Anonymization"
                                        onChange={e => setIpAnon(e.target.value as typeof ipAnon)}
                                    >
                                        <MenuItem value="hash">Hash (recommended)</MenuItem>
                                        <MenuItem value="truncate">Truncate last octet</MenuItem>
                                        <MenuItem value="discard">Discard entirely</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid size={{ xs: 12, md: 6 }}>
                                <FormControl fullWidth>
                                    <InputLabel>Data Retention</InputLabel>
                                    <Select
                                        value={dataRetention}
                                        label="Data Retention"
                                        onChange={e => setDataRetention(Number(e.target.value))}
                                    >
                                        {retentionOptions.map(d => (
                                            <MenuItem key={d} value={d}>
                                                {d} days
                                                {d === retention.defaultDays
                                                    ? ' (plan default)'
                                                    : ''}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid size={12}>
                                <Divider sx={{ my: 1 }} />
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={cookieless}
                                            onChange={e => setCookieless(e.target.checked)}
                                        />
                                    }
                                    label="Cookieless tracking (no cookies, no fingerprinting)"
                                />
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={botFiltering}
                                            onChange={e => setBotFiltering(e.target.checked)}
                                        />
                                    }
                                    label="Filter bot traffic"
                                />
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>

                <Button type="submit" variant="contained" size="large" disabled={isSaving}>
                    {isSaving ? 'Saving…' : 'Save Settings'}
                </Button>
            </Box>
            {tenant && (
                <Box sx={{ mt: 4 }}>
                    <CloseOrganisationCard tenantId={tenantId} slug={tenant.slug} />
                </Box>
            )}
        </Box>
    )
}
